
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { AccessTokenData, ApiError } from '@empathyco/playboard-core-domain'
import type { IndexJobServiceListFilters } from '../../services/interfaces'
import type { IndexClientService } from '../../services/interfaces/index-client.service'
import { computed, defineComponent, getCurrentInstance, onMounted, reactive, ref } from 'vue'

import IndexJobNewInstanceModal from '../../components/index/index-job-new-instance-modal.component.vue'
import IndexJobFilters from '../../components/index/index-jobs-filters.component.vue'
import IndexJobsTable from '../../components/index/index-jobs-table.component.vue'
import IndexSubmitJobForm from '../../components/index/index-submit-job-form.component.vue'
import IndexUpdateJobConfigForm from '../../components/index/index-update-job-config-form.component.vue'
import { UiTooltip } from '../../components/keen-ui'
import { EpbButton } from '../../components/shared/epb-button'

import { mapActions, mapGetters, useFeature } from '../../composables'

import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum } from '../../enums'
import { AUTH_CONSTS } from '../../store/auth/auth.consts'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { INDEX_CONSTS } from '../../store/index/index.consts'

export default defineComponent({
  name: 'InstanceIndexServiceConfigForm',
  components: {
    EpbButton,
    IndexJobFilters,
    IndexJobNewInstanceModal,
    IndexJobsTable,
    IndexSubmitJobForm,
    IndexUpdateJobConfigForm,
    UiTooltip,
  },
  setup(_props, { emit }) {
    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()

    const baseClass = 'epb-instance-index-service-config-form'

    const messages = $epb.messages.tools.index

    const filters = reactive<IndexJobServiceListFilters>({
      clientIds: [],
      feedIds: [],
      states: [],
    })

    const indexJobsTableRef = ref()
    const createInstanceModal = ref()
    const instance404Error = ref(false)

    const { readonly } = useFeature()

    const { currentInstance } = mapGetters<Instance>($store, CONFIG_CONSTS.moduleName, {
      currentInstance: CONFIG_CONSTS.getters.currentInstance,
    })
    const instances = computed<Array<Instance>>(() => [currentInstance.value])

    const { authTokenInfo } = mapGetters<AccessTokenData>($store, AUTH_CONSTS.moduleName, {
      authTokenInfo: AUTH_CONSTS.getters.tokenInfo,
    })

    const { getClient } = mapActions($store, INDEX_CONSTS.moduleName, {
      getClient: INDEX_CONSTS.actions.getClient,
    })

    const clientService = CONTAINER.get<IndexClientService>(TYPES.IndexClientService)

    const createInstanceVisible = computed(() => {
      return !!authTokenInfo.value?.authorization.resources.find(
        resource => resource.name.toLowerCase() === FeatureIdEnum.CREATE_INSTANCE.toLowerCase(),
      )
    })

    const showCreateInstanceButton = computed((): boolean => {
      return instance404Error.value && createInstanceVisible.value
    })

    const setFilters = (settledFilters: IndexJobServiceListFilters): void => {
      Object.assign(filters, { clientIds: [], feedIds: [], states: [] }, settledFilters)
    }

    const success = (text: string, resetTable = false): void => {
      emit('showSnackbar', text)
      if (resetTable) {
        indexJobsTableRef.value.resetTable()
      }
    }

    const error = (error: ApiError): void => {
      emit('showError', error)
    }
    const reload = (): void => {
      return indexJobsTableRef.value.fetchData()
    }

    const countDown = computed((): string => {
      return $epb.utils.compileMessage(messages.timeToReload, {
        time: indexJobsTableRef.value?.intervalTime || 0,
      })
    })

    const openCreateInstanceModal = (): void => {
      createInstanceModal.value?.open()
    }

    const createInstance = async (pluginId: string, feeds: string[]): Promise<void> => {
      try {
        await clientService.create({ name: currentInstance.value?.id, pluginId, feeds })
        emit('showSnackbar', messages.newInstanceModal.success)
        instance404Error.value = false
        createInstanceModal.value?.close()
      } catch (error) {
        emit('showError', error as ApiError)
      }
    }

    onMounted(async () => {
      if (currentInstance.value && createInstanceVisible.value) {
        try {
          await getClient(currentInstance.value.id)
        } catch (error) {
          if (error.status === 404) {
            instance404Error.value = true
          }
        }
      }
    })

    return {
      baseClass,
      filters,
      indexJobsTableRef,
      readonly,
      instances,
      success,
      error,
      setFilters,
      countDown,
      reload,
      showCreateInstanceButton,
      openCreateInstanceModal,
      createInstance,
      createInstanceModal,
      messages,
    }
  },
})
